import { useGetEssayHistory } from '@/utils/api';
import {
  AppBar,
  Dialog,
  DialogContent,
  IconButton,
  Slide,
  Stack,
  Toolbar,
  Typography
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { XIcon } from '@phosphor-icons/react';
import React, { forwardRef } from 'react';
import GradingUI from './GradingUI';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface HistoryDialogProps {
  open: boolean;
  onClose: () => void;
  historyId: string;
  versionKey: string;
  courseName: string;
  isCharacterLimit?: boolean;
}

const HistoryDialog: React.FC<HistoryDialogProps> = ({
  open,
  onClose,
  historyId,
  versionKey,
  courseName,
  isCharacterLimit = false
}) => {
  const {
    data: historyData,
    isLoading,
    error
  } = useGetEssayHistory(historyId, versionKey, { enabled: open && !!historyId && !!versionKey });

  const formatTimestamp = (isoString: string): string => {
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString([], {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return isoString;
    }
  };

  if (!open) return null;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <XIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Essay History - {courseName}
          </Typography>
        </Toolbar>
      </AppBar>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Header with timestamp */}
          <Stack spacing={1}>
            <Typography variant="h5" component="h2">
              Saved Version
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatTimestamp(versionKey)}
            </Typography>
          </Stack>

          {/* Loading/Error States */}
          {isLoading && (
            <Typography>Loading essay version...</Typography>
          )}

          {error && (
            <Typography color="error">
              Error loading essay version: {error.message}
            </Typography>
          )}

          {/* Essay Content */}
          {historyData?.data && (
            <GradingUI
              essayContent={historyData.data.tipTapJson || ''}
              score={historyData.data.score}
              generalFeedback={historyData.data.generalFeedback}
              feedback={historyData.data.feedback}
              isCharacterLimit={isCharacterLimit}
              readOnly={true}
              placeholder="This version contains no content."
            />
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default HistoryDialog; 